import React, { useCallback, useEffect, useReducer, useState } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { IStore } from '@rbi-ctg/store';
import { MobileSpacer } from 'components/mobile-spacer';
import Modal, { ModalContent } from 'components/modal';
import { GraphQLErrorMessages } from 'enums/graphql';
import { DeliveryStatus, useDeliveryRestaurantLazyQuery } from 'generated/rbi-graphql';
import usePlaceIdDetails from 'hooks/geolocation/use-place-id-details';
import useDialogModal from 'hooks/use-dialog-modal';
import useErrorModal, { IErrorModal } from 'hooks/use-error-modal';
import usePlacesService from 'hooks/use-map/use-places-service';
import {
  DeliveryStoreStatusToStoreStatusMap,
  IGetClosestAvailableDeliveryRestaurantResult,
  StoreStatus,
  useQueryClosestAvailableDeliveryRestaurant,
} from 'remote/api/restaurants';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useNetworkContext } from 'state/network';
import { ServiceMode, useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { convertMilesToMeters } from 'utils/distance';
import { platform } from 'utils/environment';
import { IPlaceData, parsePlaceAddress } from 'utils/geolocation';
import { routes } from 'utils/routing';

import { DeliveryAddresses } from './delivery-addresses';
import { DeliveryUnavailable } from './delivery-unavailable';
import DeliveryAddressForm from './new-address-form/delivery-address-form';
import { ActionStatus, AddressModalAction, SelectedDeliveryAddress } from './types';

// iOS HACK to add spacing to the bottom of the form.
// Needed because the keyboard was covering the form inputs
const SpacerContainer = styled.div`
  display: block;
  width: 100%;
`;

const ModalContentWrapper = styled(ModalContent)`
  & div[class*='modal-content__Inner'] {
    height: 100%;
    // overwrite modal content set width for delivery address styling
    width: 100%;
    max-width: 100%;
  }
`;

export const formatPhoneNumber = (phoneNumber: string) => {
  // removes all non-digit characters
  return phoneNumber.replace(/[^\d]/g, '');
};

interface IDeliveryAddressModalProps {
  redirectUrl: string;
  dismissAddressModalUrl: string;
}

interface AddressModalState {
  address: SelectedDeliveryAddress | null;
  error: IErrorModal | null;
  status: ActionStatus;
}

const addressReducer = (state: AddressModalState, action: AddressModalAction) => {
  switch (action.type) {
    case ActionStatus.IDLE:
      return { ...state, address: null, error: null, status: action.type };
    case ActionStatus.QUOTE_UNAVAILABLE: {
      return { ...state, error: null, status: action.type };
    }
    case ActionStatus.PENDING: {
      return { ...state, address: action.address, error: null, status: ActionStatus.PENDING };
    }
    case ActionStatus.ERROR: {
      return { ...state, error: action.error, status: ActionStatus.ERROR };
    }
    default: {
      throw new Error('Unrecognized state transition');
    }
  }
};

const getAddressLabel = ({ address }: SelectedDeliveryAddress) =>
  `${address.addressLine1}, ${address.city}, ${address.state} ${address.zip}, ${address.country}`;

export const DeliveryAddressModal: React.FC<IDeliveryAddressModalProps> = ({
  redirectUrl,
  dismissAddressModalUrl,
}) => {
  const [state, dispatch] = useReducer(addressReducer, {
    error: null,
    address: null,
    status: ActionStatus.IDLE,
  });
  const { user } = useAuthContext();
  const { logNavigationClick, logEvent } = useMParticleContext();
  const order = useOrderContext();
  const { setDeliverySurchargeFee } = useServiceModeContext();
  const { resetStore } = useStoreContext();
  const { formatMessage } = useIntl();
  const { connection } = useNetworkContext();
  const { checkStaticMenuItemAvailability, selectedStaticMenuItemId } = useMenuContext();
  const deliveryRadius = useFlag(LaunchDarklyFlag.DELIVERY_RADIUS_IN_MILES);
  const enableConsolidatedDeliveryRestaurantLogic = useFlag(
    LaunchDarklyFlag.ENABLE_CONSOLIDATED_DELIVERY_RESTAURANT_BACKEND_LOGIC
  );
  const enableStreetNumberAfterStreetName = useFlag(
    LaunchDarklyFlag.ENABLE_STREET_NUMBER_AFTER_STREET_NAME
  );
  const enableThirdPartyRedirect = useFlag(
    LaunchDarklyFlag.ENABLE_THIRD_PARTY_REDIRECT_UNAVAILABLE_DELIVERY_MODAL
  );
  const [
    queryDeliveryRestaurant,
    { data: deliveryRestaurantData },
  ] = useDeliveryRestaurantLazyQuery({ fetchPolicy: 'no-cache' });
  const [
    queryRestaurantsFromBackend,
    storeData,
    deliveryQuoteError,
  ] = useQueryClosestAvailableDeliveryRestaurant();
  const logger = useLogger();
  const placesService = usePlacesService();
  const getDetails = usePlaceIdDetails();
  const navigate = useNavigate();

  const isLocatorUIRefreshEnabled = useFlag(LaunchDarklyFlag.ENABLE_LOCATOR_UI_REFRESH);

  const enableUserSavedDeliveryAddressPhone = useFlag(
    LaunchDarklyFlag.ENABLE_USER_SAVED_DELIVERY_ADDRESS_PHONE
  );
  const shouldEnableUserSavedDeliveryAddressPhone = enableUserSavedDeliveryAddressPhone && !!user;

  const showDeliveryAddresses =
    shouldEnableUserSavedDeliveryAddressPhone && Boolean(user?.details?.deliveryAddresses?.length);

  const [createAddressView, setCreateAddressView] = useState(false);
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Delivery Not Completed',
  });

  const [AlertDialog, openAlert, alertMessage] = useDialogModal({
    onConfirm: () => navigate(`${routes.account}/info`),
    modalAppearanceEventMessage: 'Alert: Payment Failed',
  });

  const getPlaceId = useCallback(
    (addrString: string) => {
      return new Promise<string | undefined>((resolve, reject) => {
        const currentPlaces = placesService();
        if (!currentPlaces) {
          return;
        }

        currentPlaces.findPlaceFromQuery(
          { query: addrString, fields: ['place_id'] },
          (results, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !results[0]) {
              return reject('Address not recognized');
            }
            return resolve(results[0].place_id!);
          }
        );
      });
    },
    [placesService]
  );

  const getDetailsPlaceData = useCallback(
    (pId: string | undefined): Promise<IPlaceData> => {
      return getDetails(pId).then(details => {
        if (details) {
          const placeData = {
            coordinates: {
              lat: details.geometry!.location.lat(),
              lng: details.geometry!.location.lng(),
            },
            address: parsePlaceAddress(details.address_components, {
              useISOCountry: true,
              streetNameFirst: enableStreetNumberAfterStreetName,
            }),
          };

          return Promise.resolve(placeData);
        }

        logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE, {
          error: 'Address Not Recognized',
        });

        return Promise.reject(details);
      });
    },
    [enableStreetNumberAfterStreetName, getDetails, logNavigationClick]
  );

  const {
    setDeliveryAddress,
    setDeliveryInstructions,
    setOrderPhoneNumber,
    setQuoteId,
    selectStore,
    selectServiceMode,
  } = order;

  const handleQuoteResult = useCallback(
    async ({
      store,
      deliveryQuote,
      storeStatus,
      nextEarliestOpen,
    }: IGetClosestAvailableDeliveryRestaurantResult) => {
      // if store available, route to store
      if (store && deliveryQuote === DeliveryStatus.QUOTE_SUCCESSFUL) {
        return selectStore(
          store,
          async () => {
            if (state.address) {
              const {
                address,
                shouldSaveAddress,
                deliveryInstructions,
                phoneNumber,
                coordinates,
              } = state.address;

              setDeliveryAddress({
                ...address,
                shouldSave: shouldSaveAddress,
                latitude: coordinates?.lat,
                longitude: coordinates?.lng,
              });

              setDeliveryInstructions(deliveryInstructions);
              setOrderPhoneNumber(phoneNumber);
            }

            selectServiceMode(ServiceMode.DELIVERY);
            // If user selected item from static menu, make sure it is available at store
            if (selectedStaticMenuItemId) {
              await checkStaticMenuItemAvailability(store, ServiceMode.DELIVERY);
              return;
            }

            navigate(redirectUrl);
          },
          ServiceMode.DELIVERY
        );
      }

      if (deliveryQuote === DeliveryStatus.QUOTE_UNAVAILABLE) {
        return dispatch({ type: ActionStatus.QUOTE_UNAVAILABLE });
      }

      if (deliveryQuote === DeliveryStatus.QUOTE_ERROR) {
        return dispatch({
          type: ActionStatus.ERROR,
          error: {
            message: formatMessage({ id: 'pleaseTryAgainLater' }),
            modalAppearanceEventMessage: 'Error: Delivery Quote Error',
          },
        });
      }

      // clear service mode and the store if delivery is not available
      if (!isLocatorUIRefreshEnabled && storeStatus === StoreStatus.NO_DELIVERY) {
        selectServiceMode(null);
        resetStore();
      }

      if (storeStatus === StoreStatus.NO_DELIVERY && enableThirdPartyRedirect) {
        return dispatch({ type: ActionStatus.QUOTE_UNAVAILABLE });
      }
      // otherwise present current store status modal
      navigate(routes[storeStatus], { state: { nextEarliestOpen } });
    },
    [
      checkStaticMenuItemAvailability,
      formatMessage,
      isLocatorUIRefreshEnabled,
      navigate,
      redirectUrl,
      resetStore,
      selectServiceMode,
      selectStore,
      selectedStaticMenuItemId,
      setDeliveryAddress,
      setDeliveryInstructions,
      setOrderPhoneNumber,
      state.address,
    ]
  );

  const completeDelivery = async (data: SelectedDeliveryAddress) => {
    if (state.status === ActionStatus.PENDING) {
      return;
    }

    if (!data.address) {
      return dispatch({
        type: ActionStatus.ERROR,
        error: { message: 'Missing address information for delivery' },
      });
    }

    const {
      address,
      phoneNumber,
      coordinates: selectedCoordinates,
      placeId: selectedPlaceId,
    } = data;

    dispatch({ type: ActionStatus.PENDING, address: data });

    try {
      let placeId = selectedPlaceId;

      if (!selectedCoordinates && !selectedPlaceId) {
        // only get the placeId if we need it to look up coordinates
        placeId = await getPlaceId(getAddressLabel(data));
      }

      const { coordinates } = selectedCoordinates
        ? { coordinates: selectedCoordinates }
        : await getDetailsPlaceData(placeId);

      if (enableConsolidatedDeliveryRestaurantLogic) {
        queryDeliveryRestaurant({
          variables: {
            dropoff: {
              ...address,
              latitude: coordinates.lat,
              longitude: coordinates.lng,
              phoneNumber,
            },
            searchRadius: convertMilesToMeters(deliveryRadius),
            platform: platform(),
          },
        });
      } else {
        queryRestaurantsFromBackend(
          phoneNumber,
          deliveryRadius,
          {
            connection,
            userLat: coordinates.lat,
            userLng: coordinates.lng,
          },
          address
        );
      }
    } catch (error) {
      dispatch({
        type: ActionStatus.ERROR,
        error: { message: 'Error complete Delivery', error },
      });
    }
  };

  useEffect(() => {
    if (deliveryQuoteError && deliveryQuoteError === GraphQLErrorMessages.INVALID_PHONE_NUMBER) {
      openAlert({
        body: formatMessage({ id: 'areDeliveryDetailsValid' }),
        buttonLabel: formatMessage({ id: 'addPhoneNumber' }),
        heading: formatMessage({ id: 'deliveryPhoneNumberError' }),
      });
      return;
    }

    // TODO: Consider isolating this to it's own effect.
    if (storeData) {
      handleQuoteResult(storeData);
    }

    if (deliveryRestaurantData?.deliveryRestaurant && enableConsolidatedDeliveryRestaurantLogic) {
      const {
        quote,
        restaurant,
        storeStatus,
        nextEarliestOpen,
        deliverySurchargeFeeCents,
        quoteId,
      } = deliveryRestaurantData?.deliveryRestaurant;

      if (deliverySurchargeFeeCents) {
        setDeliverySurchargeFee(deliverySurchargeFeeCents);
      }

      setQuoteId(quoteId);

      handleQuoteResult({
        deliveryQuote: quote,
        store: (restaurant as unknown) as IStore,
        storeStatus: DeliveryStoreStatusToStoreStatusMap[storeStatus],
        nextEarliestOpen: nextEarliestOpen ? new Date(nextEarliestOpen) : undefined,
      });

      logger.info({
        message: quote,
        status: restaurant?.status,
      });
      logEvent(CustomEventNames.FIRST_DELIVERY_QUOTE, EventTypes.Other, {
        status: quote,
        'Restaurant Id': restaurant?._id,
        'Restaurant Name': restaurant?.name,
      });
    }

    /*
     * TODO: `handleQuoteResult` is a created by `useCallback`. It's updated
     * whenever `selectServiceMode` is updated, which is also created by
     * `useCallback`. `selectServiceMode` is updated whenever the `serviceMode`
     * state variable is changed. `selectServiceMode` is called in
     * `handleQuoteResult` causing an infinite render loop.
     *
     * Leaving `handleQuoteResult` out of the dependency array solves the issue
     * for now, but might needs a closer look as we organize state more effectively.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableConsolidatedDeliveryRestaurantLogic,
    deliveryRestaurantData,
    deliveryQuoteError,
    storeData,
  ]);

  const handleDismiss = () => {
    navigate(dismissAddressModalUrl);
  };

  useEffect(() => {
    if (state.status === ActionStatus.ERROR && state.error) {
      openErrorDialog(state.error);
    }
  }, [openErrorDialog, state.error, state.status]);

  return (
    <>
      <Modal
        onDismiss={handleDismiss}
        mParticleEventData={{
          modalAppearanceEventMessage: 'Select your delivery address',
        }}
        skipLoggingHeaderAndMessage={true}
        data-testid="delivery-modal"
      >
        <ModalContentWrapper>
          {state.status === ActionStatus.QUOTE_UNAVAILABLE ? (
            <DeliveryUnavailable
              address={state.address ? getAddressLabel(state.address) : ''}
              close={() => dispatch({ type: ActionStatus.IDLE })}
            />
          ) : (
            <SpacerContainer>
              {showDeliveryAddresses && !createAddressView ? (
                <DeliveryAddresses
                  addresses={user?.details?.deliveryAddresses || []}
                  onCreate={() => setCreateAddressView(true)}
                  setAddress={completeDelivery}
                  isLoading={state.status === ActionStatus.PENDING}
                />
              ) : (
                <DeliveryAddressForm
                  instructions={order.deliveryInstructions}
                  getDetailsPlaceData={getDetailsPlaceData}
                  setCreateAddressView={setCreateAddressView}
                  showDeliveryAddresses={showDeliveryAddresses}
                  user={user}
                  setAddress={completeDelivery}
                  isLoading={state.status === ActionStatus.PENDING}
                />
              )}
              <MobileSpacer />
            </SpacerContainer>
          )}
        </ModalContentWrapper>
        <ErrorDialog data-testid="address-modal-error-dialog" />
        <AlertDialog {...(alertMessage || {})} />
      </Modal>
    </>
  );
};
